<!--
Copyright (c) 2020 ForgeRock. All rights reserved.

This software may be modified and distributed under the terms
of the MIT license. See the LICENSE file for details.
-->

<template>
    <div :class="themeName.toLowerCase() === 'ged' ? 'ged-main' : 'main-div'">
        <pearson-nav :themeName="themeName" :showBrandBadge="ui.showBrandBadge" :showNavRegion="ui.showNavRegion" />

        <div :class="[`${themeName.toLowerCase()}-background-image`, 'page-container']" :data-type="selfServiceType">

            <pearson-logo :logoType="ui.logoType" :logoSrc="ui.logo || pearsonLogoUrl()" :logoAlt="ui.logoAlt"
                :logoWidth="ui.logoWidth" :logoHeight="ui.logoHeight" :logoClass="ui.logoClass" :logoId="ui.logoId"
                :logoContainerClass="ui.logoContainerClass" :logoImages="ui.logoImages" :welcomeText="welcomeText" />

            <fr-center-card :class="[
                { 'faethm-right-align-div': themeName === 'FAETHM' },
                { 'reset-stage-card': (selfServiceType === 'emailValidation' || selfServiceType === 'resetStage') && !isEmailLink },
                { 'email-validation-card': selfServiceType === 'emailValidation' && !isEmailLink },
                { 'reset-stage-height': selfServiceType === 'resetStage' && !isEmailLink },
                'pearson-card'
            ]" v-if="selfServiceType !== null && selfServiceType !== 'parameters'" :showLogo="false">
                <div slot="center-card-header">
                    <div v-if="themeName === 'FAETHM'">
                        <img class="main-logo side-logo faethm-pearson-logo" :src="ui.logo" width="140px" />
                    </div>
                    <h1 class="h2" id="forgot-pwd"
                        v-if="(selfServiceType !== 'resetStage' && selfServiceType !== 'emailValidation') || isEmailLink">
                        {{ titleText }}</h1>
                    <h2 :class="[isAccountLinking ? 'titleHeaderOne' : 'h2']" id="forgot-pwd" v-else>{{ this.$route.query.isLinkingAccounts === 'true'
                                                                             ? this.$t('pages.selfservice.headers.reset.linkingAccountsTitle')
                                                                             : this.$t('pages.selfservice.headers.reset.emailQ') }} </h2>
                    <h2 class="h2" id="set-pwd" style="display:none">
                        {{ $t(`pages.selfservice.headers.reset.setPasswordTitle`) }}
                    </h2>
                </div>

                <b-card-body slot="center-card-body">
                    <component ref="selfServiceStage" :is="selfServiceType" :selfServiceDetails="selfServiceDetails"
                        @advanceStage="advanceStage" :apiType="apiType">
                    </component>
                    <div class="ged-need-help-div">
                    <a id="needhelp" v-if="(selfServiceType !== emailValidation)&& themeName==='GED'"
                        :class="'ged-need-help'"
                        :href="'https://www.ged.com/tips-successful-password-resets/'">{{ $t('pages.selfservice.passwordReset.needHelp') }}</a>
                        </div>
                    <a id="custom-password" v-if="(selfServiceType !== emailValidation) && hasBackButton"
                        :class="['back', { 'ged-back-link': themeName === 'GED','faethm-back-link': themeName === 'FAETHM' }]" @click="backUrl"
                        :href="gotoLink">{{ $t('pages.selfservice.passwordReset.returnToSignInAnchorText') }}</a>
                </b-card-body>
            </fr-center-card>
            <b-container fluid class="h-100 px-0" v-else>
                <div class="h-100 d-flex">
                    <div class="m-auto fr-center-card">
                        <bounce-loader :color="loadingColor"></bounce-loader>
                    </div>
                </div>
            </b-container>
        </div>

        <pearson-footer :themeName="themeName" :showFooter="ui.showFooter" :links="links" :ui="ui" />
    </div>
</template>

<script>
import _ from 'lodash';
import { BounceLoader } from 'vue-spinner/dist/vue-spinner.min.js';
import styles from '@/scss/theme-variables.scss';
import CenterCard from '@/components/utils/CenterCard';
import SelfserviceAPI from '@/components/selfservice/mixins/SelfserviceAPIMixin';
import ThemeMixin from '../../../components/common/mixins/ThemeMixin';
import { getClientById } from '../../../lib/getClientById';

// Stage shells load on demand so the password-reset route chunk does not pull every flow
// (KBA, SMS OTP, email validation, captcha) before /selfservice/reset returns.
const PASSWORD_RESET_STAGE_TYPES = [
    'captcha',
    'emailvalidation',
    'userquery',
    'resetselfservice',
    'resetstage',
    'verifysmsotp',
    'kbasecurityanswerverificationstage'
];

/**
 * @description Selfservice controlling component for recovering a lost password. Makes use of selfservice-reset.json config file.
 *
 * @mixin - selfservice/mixins/SelfserviceAPIMixin.vue
 */
export default {
    name: 'Password-Reset',
    components: {
        Captcha: () => import('@/components/selfservice/common/Captcha'),
        EmailValidation: () => import('@/components/selfservice/common/EmailValidation'),
        UserQuery: () => import('@/components/selfservice/common/UserQuery'),
        ResetStage: () => import('@/components/selfservice/passwordreset/ResetStage'),
        VerifySMSOTP: () => import('@/components/selfservice/passwordreset/VerifySMSOTP'),
        kbaSecurityAnswerVerificationStage: () => import('@/components/selfservice/passwordreset/KbaVerification'),
        ResetSelfService: () => import('@/components/selfservice/passwordreset/ResetSelfService'),
        'bounce-loader': BounceLoader,
        'fr-center-card': CenterCard,
        PearsonNav: () => import('../../../components/common/layout/PearsonNav.vue'),
        PearsonLogo: () => import('../../../components/common/layout/PearsonLogo.vue'),
        PearsonFooter: () => import('../../../components/common/layout/PearsonFooter.vue')
    },
    computed: {
        titleText () {
            const clientId = this.getQueryStringValue('client_id') || getClientById();
            return this.getTitleText(clientId);
        },
        hasBackButton () {
            return !this.$route.params?.queryParams?.includes('token=') && !this.$route.query?.token;
        },
        gotoLink () {
            return this.sanitizeUrl(this.$route.query.goto) || null;
        }
    },
    data () {
        const clientId = this.getQueryStringValue
            ? (this.getQueryStringValue('client_id') || this.getQueryStringValue('clientId') || getClientById())
            : getClientById();
        return {
            selfServiceType: null,
            selfServiceDetails: null,
            loading: false,
            isEmailLink: false,
            loadingColor: styles.baseColor,
            apiType: 'reset',
            welcomeText: this.getWelcomeText(clientId),
            isAccountLinking: this.getQueryStringValue('isLinkingAccounts') === 'true'
        };
    },
    created () {
        const hash = window.location.hash.substring(1);
        const firstAmp = hash.indexOf('&');
        if (this.$route.params.queryParams && firstAmp !== -1) {
            const queryParams = this.parseQueryParams(hash.substring(firstAmp));
            const { token: emailLinkParam } = queryParams;
            if (emailLinkParam && emailLinkParam !== '') {
                this.isEmailLink = true;
            }
            this.advanceStage(queryParams);
        } else {
            this.loadData();
        }
    },
    mounted () {
        if (this.themeName === 'GED') {
            const mq = window.matchMedia('(max-width: 428px) and (max-height: 926px)');
            this.updateLogo(mq);
            mq.addEventListener('change', this.updateLogo);
        }
        if (this.isAccountLinking) {
            this.$nextTick(() => {
                const title = document.getElementById('forgot-pwd');
                if (title && title.textContent.trim() === 'Create a Pearson password') {
                    title.style.width = '388px';
                    title.style.height = '36px';
                    const cardHeader = document.querySelector('.card-header.d-flex.align-items-center.flex-fill');
                    if (cardHeader) {
                        cardHeader.style.paddingLeft = '30px';
                    }
                }
                const alert = document.querySelector('.alert.no-bg.no-border.alert-info');
                if (alert) {
                    alert.className = '';
                    alert.classList.add('titleHeaderTwo');
                }
            });
            this.$watch('selfServiceType', () => {
                this.$nextTick(() => this.applyAccountLinkingStyles());
            }, { immediate: true });
        }
        this.updateDocumentTitle();
    },
    watch: {
        titleText () {
            this.updateDocumentTitle();
        },
        '$i18n.locale' () {
            this.updateDocumentTitle();
        }
    },
    methods: {
        updateDocumentTitle () {
            const translatedTitle = this.titleText || this.$t('pages.selfservice.headers.reset.title');
            const brandSuffix = 'Pearson';
            if (translatedTitle) {
                document.title = `${translatedTitle} | ${brandSuffix}`;
            }
        },
        applyAccountLinkingStyles () {
            const card = document.querySelector('.card');
            if (card) {
                card.classList.add('new-card');
            }
            const cardBody = document.querySelector('.fr-center-card.card-body');
            if (cardBody) {
                cardBody.classList.add('new-card-body');
            }
            const backBtn = document.getElementById('custom-password');
            if (backBtn) {
                backBtn.className = '';
                backBtn.classList.add('new-card-back');
            }
            document.querySelectorAll('form label').forEach((label) => {
                label.classList.add('new-sub-label');
            });
        },
        sanitizeUrl (url) {
            if (!url || typeof url !== 'string') {
                return null;
            }
            const backtoSigninUrl = url.toLowerCase();
            const sanitizedUrl = [
                'javascript:',
                'data:',
                'vbscript:',
                'eval(',
                'atob(',
                'fetch(',
                'xmlhttprequest',
                // Check for the specific base64 encoded payload
                'zmv0y2goimg0dhbzoi8vaWftlxn0ywdllnblyxjzb24uy29tl2f1dgganzvbi9yzwfsbxmvcm9vdc9yzwfsbxmvcgvhcnivbi91c2vycu9fyWn0aw9upwlkrnhvbvnlc3npb24i'
            ];
            for (let urlParams of sanitizedUrl) {
                if (backtoSigninUrl.includes(urlParams)) {
                    return null;
                }
            }
            const cleanUrl = url.replace(/javascript:/gi, '')
                .replace(/data:/gi, '')
                .replace(/vbscript:/gi, '')
                .replace(/on\w+=/gi, '');
            const urlPattern = /^(https?:\/\/|\/|\.\/|\?)/i;
            if (!urlPattern.test(cleanUrl.trim())) {
                return null;
            }
            // Additional regex check for XSS patterns
            const xssURLPatterns = [
                /<script/gi,
                /on\w+\s*=/gi,
                /expression\(/gi,
                /eval\(/gi,
                /atob\(/gi,
                /fetch\(/gi,
                // Regex for the specific base64 payload
                /ZmV0Y2goImh0dHBzOi8vaWFtLXN0YWdlLnBlYXJzb24uY29tL2F1dGgvanNvbi9yZWFsbXMvcm9vdC9yZWFsbXMvcGVhcnNvbi91c2Vycz9fYWN0aW9uPWlkRnJvbVNlc3Npb24i/gi
            ];
            for (let pattern of xssURLPatterns) {
                if (pattern.test(cleanUrl)) {
                    return null;
                }
            }
            return cleanUrl.trim();
        },
        /**
         * Get a cookie value by name
         * @param {string} name - Cookie name
         * @returns {string|null} - Cookie value or null if not found
         */
        updateLogo (e) {
            if (e.matches) {
                this.ui.logo = require('@/assets/images/GED_mobile_logo.png');
            } else {
                this.ui.logo = require('@/assets/images/GED_LOGO.png');
            }
        },
        backUrl (event) {
            const goToUrl = this.sanitizeUrl(this.$route.query.goto);
            if (!goToUrl) {
                event.preventDefault();
                window.location.assign(window.location.origin + '/auth/XUI/?realm=/pearson&service=AuthN#login/');
            }
        },
        setChildComponent (type, details) {
            this.selfServiceDetails = details;
            if (type === 'parameters') {
                this.selfServiceType = null;
                this.advanceStage({});
            } else {
                const stageCheck = PASSWORD_RESET_STAGE_TYPES.includes(_.toLower(type));

                if (stageCheck) {
                    this.selfServiceType = type;
                } else if (details.type === 'localAutoRedirect') {
                    this.selfServiceType = 'resetStage';
                } else {
                    this.selfServiceType = 'ResetSelfService';
                }
            }
        },
        isExpiredLinkMessage (message) {
            const expiredLinkMessages = [
                'Snapshot token is invalid',
                'Token expired',
                'Invalid token'
            ];
            const normalizeMessage = (msg) => (msg || '').trim().toLowerCase();

            return expiredLinkMessages.some(
                (expiredMessage) => normalizeMessage(expiredMessage) === normalizeMessage(message)
            );
        },
        apiErrorCallback (error) {
            this.loading = false;
            const message = error.response === undefined
                ? this.$t('pages.selfservice.passwordReset.errorMessage')
                : error.response.data.message;
            if (this.isExpiredLinkMessage(message)) {
                this.setChildComponent('resetStage', { error: this.$t('pages.selfservice.headers.reset.forgotPasswordLinkExpired'), message });
            } else {
                this.setChildComponent('resetStage', { error: message, message });
            }
        },
        getWelcomeText (clientId) {
            const setPasswordKey = this.getQueryStringValue('setPassword');
            return clientId === 'PVS-Client' && setPasswordKey === 'true' ? this.$t('pages.selfservice.passwordReset.welcomeTextOne') : this.$t('pages.selfservice.passwordReset.welcomeText');
        },
        getTitleText (clientId) {
            const setPasswordKey = this.getQueryStringValue('setPassword');
            const isLinkingAccounts = this.$route.query.isLinkingAccounts === 'true';
            return clientId === ('PVS-Client' && setPasswordKey === 'true') || isLinkingAccounts
                ? this.$t('pages.selfservice.headers.reset.titleOne')
                : this.$t('pages.selfservice.headers.reset.title');
        }
    },
    mixins: [
        SelfserviceAPI,
        ThemeMixin
    ]
};
</script>
<style scoped lang="scss">
@import '../../../scss/pearson-common.css';

.email-validation-card {
    height: 256px;
}

.reset-stage-height {
    height: 425px;
}
</style>
